import React, {useEffect, useMemo, useState} from 'react';
import {BaselineCmdsInfo} from '../utils/baselineCmds';
import {
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	IDialogContentProps,
	PrimaryButtonCompat as PrimaryButton,
	TextFieldCompat as TextField,
} from 'components/FluentUICompat';
import {useVehicleProjectsTranslation} from 'features/VehicleProjects/hooks/vehicleProjectsTranslationHooks';
import {
	NavigateToBaselineListPage,
	useNavigateToBaselineListPage,
} from '../BaselineListPage/BaselinesPage.utils';
import {
	GetBaselinesByModelAndSeriesDocument,
	useCreateBaseLineByModelAndSeriesMutation,
	useCreateRequirementsLibraryBaseLineMutation,
} from '../hooks/baseline.generated';
import {ControlledCheckbox} from 'components/hookForms';
import {useBaselineTransferForm} from '../hooks/useBaselineTransferForm';
import { getIsRequirementLibrary } from 'features/Requirements/components/RequirementsBaseline';

interface Props {
	generation: string;
	modelSeries: string;
	infoForIsOpen: BaselineCmdsInfo['infoForIsSuccessDialogOpen'];
}

export function BaselineCreateDialog({
	generation,
	modelSeries,
	infoForIsOpen: [isOpen, {setFalse}],
}: Props): React.ReactElement {

	const isRequirementsLibrary = getIsRequirementLibrary();
	const {t} = useVehicleProjectsTranslation({
		keyPrefix: 'BaselineCreateDialog',
	});

	const now = new Date();

	const getBaselineNamePrefix = () => {
		if (isRequirementsLibrary) {
			return 'TR:ACE Anforderungsliste';
		}
		return `TR:ACE Anforderungsliste für Modellreihe: ${modelSeries}, Generation: ${generation}`;
	};

	const defaultName = `${getBaselineNamePrefix()} vom ${now.toLocaleDateString(
		'de-DE',
	)} um ${now.toLocaleTimeString('default', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})} Uhr`;

	const transferForm = useBaselineTransferForm();
	const [name, setName] = useState(defaultName);
	const [creating, setCreating] = useState(false);

	const navigateToBaselinesPage: NavigateToBaselineListPage =
		useNavigateToBaselineListPage({
			generation,
			modelSeries,
		});

	const [createBaseLineByModelAndSeriesMutation] =
		useCreateBaseLineByModelAndSeriesMutation();

	const [
		createRequirementsLibraryBaseLineMutation,
		{loading: creatingBaseline},
	] = useCreateRequirementsLibraryBaseLineMutation();

	const onTitleChange = (
		event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
		newValue?: string,
	) => {
		setName(newValue ?? '');
	};

	const handleSave = async () => {
		try {
			setCreating(true);

			const status = transferForm.getStatus();

			if (isRequirementsLibrary) {
				await createRequirementsLibraryBaseLineMutation({
					variables: {
						input: {
							name: defaultName,
							onlyApprovedRequirements: !status,
						},
					},
					refetchQueries: [GetBaselinesByModelAndSeriesDocument],
				});
			} else {
				await createBaseLineByModelAndSeriesMutation({
					variables: {
						input: {
							name,
							generation,
							modelSeries,
							transferWorkflow: {
								transferToCodebeamer: status,
							},
						},
					},
					refetchQueries: [GetBaselinesByModelAndSeriesDocument],
				});
			}
		} catch (error) {
			console.error('Failed to rename baseline:', error);
		}

		setFalse();

		navigateToBaselinesPage();
		setCreating(false);
	};

	const handleCancel = async () => {
		setFalse();
	};

	const dialogContentProps: IDialogContentProps = useMemo(() => {
		return {
			type: DialogType.normal,
			title: t('Title'),
			styles: {title: {wordBreak: 'break-word'}},
		};
	}, [t]);
	const checkboxStyles = {root: {paddingLeft: '5px', paddingTop: '5px'}};

	const isNameEmpty = !name;

	return (
		<Dialog
			hidden={!isOpen}
			onDismiss={setFalse}
			dialogContentProps={dialogContentProps}
		>
			<TextField
				value={name}
				onChange={onTitleChange}
				multiline
				required
				styles={{fieldGroup: {minHeight: '200px'}}}
				errorMessage={isNameEmpty ? t('EmptyNameError') : undefined}
			/>
			<ControlledCheckbox
				name={
					isRequirementsLibrary
						? 'OnlyApprovedRequirements'
						: 'CodebeamerTransfer'
				}
				label={
					isRequirementsLibrary
						? t('OnlyApprovedRequirements')
						: t('CodebeamerTransfer')
				}
				control={transferForm.control}
				styles={checkboxStyles}
			/>
			<DialogFooter>
				<PrimaryButton onClick={handleCancel} disabled={false}>
					{t('CancelBtn')}
				</PrimaryButton>

				<PrimaryButton onClick={handleSave} disabled={isNameEmpty || creating}>
					{t('SaveBtn')}
				</PrimaryButton>
			</DialogFooter>
		</Dialog>
	);
}
